import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toPlainText, type Conversation, type SlackUser } from '@assembly/slack'
import { useActions } from '../data/actions'
import { displayName, fullName, useConversationName, useConversations, useExternalOrg, usePublicChannels, useUsers } from '../data/hooks'
import { useMe } from '../data/workspace'
import { useUi } from '../state/ui'
import { highlightRuns, parseQuery, rankSwitcher, type SwitcherCandidate, type SwitcherMatch } from '../switcher'
import { ConversationIcon } from './ChannelView'
import { Icon } from './Icon'
import { Avatar, Emoji, Spinner } from './common'

interface Candidate extends SwitcherCandidate {
  conv?: Conversation
  user?: SlackUser
  detail?: string
}

type Row = { type: 'match'; match: SwitcherMatch<Candidate> } | { type: 'search'; query: string }

const close = () => useUi.getState().openModal(null)

/** Builds every place you can jump to: your conversations, everyone in the directory, and joinable channels. */
function useCandidates(): { candidates: Candidate[]; loading: boolean } {
  const { data: convs, isLoading: loadingConvs } = useConversations()
  const { data: users, isLoading: loadingUsers } = useUsers()
  const { data: publicChannels } = usePublicChannels()
  const name = useConversationName()
  const me = useMe()
  const recent = useUi((s) => s.recent)
  const unread = useUi((s) => s.unread)
  const mentions = useUi((s) => s.mentions)
  const channelId = useUi((s) => s.channelId)
  const view = useUi((s) => s.view)
  const currentId = view.type === 'channel' ? channelId : null

  const candidates = useMemo(() => {
    const out: Candidate[] = []
    const memberIds = new Set<string>()
    const dmUsers = new Set<string>()
    const byHandle = new Map([...(users?.values() ?? [])].map((u) => [u.name, u]))
    const state = (id: string) => ({ recentAt: recent[id], unread: unread[id], mentions: mentions[id], current: id === currentId })
    const about = (c: Conversation) => [c.topic?.value, c.purpose?.value].map((v) => (v ? toPlainText(v).replace(/\s+/g, ' ').trim() : '')).filter(Boolean)
    const personText = (u: SlackUser | undefined) => ({
      aliases: u ? [fullName(u), u.name, u.profile?.display_name ?? ''].filter(Boolean) : [],
      keywords: u?.profile?.title ? [u.profile.title] : [],
    })

    for (const c of convs ?? []) {
      memberIds.add(c.id)
      if (c.is_im) {
        const u = users?.get(c.user ?? '')
        if (c.user) dmUsers.add(c.user)
        out.push({ id: c.id, kind: 'dm', label: name(c), conv: c, user: u, inactive: !!(u?.deleted || c.is_user_deleted), ...personText(u), ...state(c.id) })
      } else if (c.is_mpim) {
        const members = (c.name ?? '').replace(/^mpdm-/, '').replace(/-\d+$/, '').split('--').map((h) => byHandle.get(h))
        out.push({ id: c.id, kind: 'group', label: name(c), conv: c, aliases: members.flatMap((u) => (u ? [fullName(u), u.name] : [])), ...state(c.id) })
      } else {
        out.push({
          id: c.id,
          kind: 'channel',
          label: c.name ?? c.id,
          conv: c,
          keywords: about(c),
          detail: about(c)[0],
          inactive: c.is_archived,
          ...state(c.id),
        })
      }
    }

    // People you haven't messaged yet: selecting one opens a new DM.
    for (const u of users?.values() ?? []) {
      if (u.deleted || dmUsers.has(u.id) || u.id === 'USLACKBOT') continue
      const label = u.id === me ? `${displayName(u)} (you)` : displayName(u)
      out.push({ id: u.id, kind: 'person', label, user: u, ...personText(u) })
    }

    // Public channels you're not in: selecting one previews it, with a Join bar.
    for (const c of publicChannels ?? []) {
      if (memberIds.has(c.id) || c.is_member) continue
      out.push({
        id: c.id,
        kind: 'browse',
        label: c.name ?? c.id,
        conv: c,
        keywords: about(c),
        detail: about(c).at(-1),
        inactive: c.is_archived,
      })
    }
    return out
  }, [convs, users, publicChannels, name, me, recent, unread, mentions, currentId])

  return { candidates, loading: loadingConvs || loadingUsers }
}

export function QuickSwitcher({ initialQuery = '' }: { initialQuery?: string }) {
  const [query, setQuery] = useState(initialQuery)
  const [selected, setSelected] = useState(0)
  const [opening, setOpening] = useState<string | null>(null)
  const { candidates, loading } = useCandidates()
  const actions = useActions()
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLDivElement>(null)

  const rows = useMemo<Row[]>(() => {
    const matches = rankSwitcher(candidates, query, Date.now(), query.trim() ? 40 : 12)
    const rows: Row[] = matches.map((match) => ({ type: 'match', match }))
    const { text } = parseQuery(query)
    if (text) rows.push({ type: 'search', query: query.trim().replace(/^[#@]/, '') })
    return rows
  }, [candidates, query])

  // New query → back to the best match.
  useEffect(() => setSelected(0), [query])
  useEffect(() => {
    if (selected >= rows.length) setSelected(Math.max(0, rows.length - 1))
  }, [rows.length, selected])

  useLayoutEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-index="${selected}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [selected, rows])

  useEffect(() => {
    const el = input.current
    if (!el) return
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
  }, [])

  const choose = async (row: Row | undefined) => {
    if (!row || opening) return
    const ui = useUi.getState()
    if (row.type === 'search') {
      close()
      ui.setView({ type: 'search', query: row.query })
      return
    }
    const { item } = row.match
    if (item.kind === 'person') {
      setOpening(item.id)
      try {
        await actions.openDirectMessage([item.id])
        close()
      } catch {
        setOpening(null) // the action showed a toast
      }
      return
    }
    close()
    ui.openChannel(item.id)
  }

  const move = (delta: number) => {
    if (!rows.length) return
    setSelected((s) => (s + delta + rows.length) % rows.length)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return
    const ctrlOnly = e.ctrlKey && !e.metaKey && !e.altKey
    if (e.key === 'ArrowDown' || (ctrlOnly && (e.key === 'n' || e.key === 'j')) || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault()
      move(1)
    } else if (e.key === 'ArrowUp' || (ctrlOnly && (e.key === 'p' || e.key === 'k')) || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault()
      move(-1)
    } else if (e.key === 'PageDown') {
      e.preventDefault()
      setSelected((s) => Math.min(rows.length - 1, s + 8))
    } else if (e.key === 'PageUp') {
      e.preventDefault()
      setSelected((s) => Math.max(0, s - 8))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      choose(rows[selected])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      if (query) setQuery('')
      else close()
    }
  }

  const empty = !query.trim()
  const scope = parseQuery(query).only
  const placeholder = scope === 'channels' ? 'Find a channel…' : scope === 'people' ? 'Find a person…' : 'Jump to a conversation or person…'
  const activeId = rows[selected] ? `switcher-row-${selected}` : undefined

  return createPortal(
    <div className="modal-backdrop switcher-backdrop" onPointerDown={(e) => e.target === e.currentTarget && close()}>
      <div className="switcher" role="dialog" aria-modal="true" aria-label="Jump to">
        <div className="switcher-input">
          <Icon name="search" size={18} />
          <input
            ref={input}
            value={query}
            placeholder={placeholder}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            role="combobox"
            aria-expanded="true"
            aria-controls="switcher-list"
            aria-activedescendant={activeId}
            aria-label="Jump to"
          />
          {loading && <Spinner size={16} />}
          <kbd className="switcher-esc">esc</kbd>
        </div>

        <div className="switcher-list" id="switcher-list" role="listbox" ref={list}>
          {empty && rows.length > 0 && <div className="switcher-heading">{scope === 'channels' ? 'Channels' : 'Recent'}</div>}
          {rows.map((row, i) =>
            row.type === 'search' ? (
              <SearchRow key="search" index={i} query={row.query} selected={i === selected} onHover={setSelected} onChoose={() => choose(row)} />
            ) : (
              <MatchRow
                key={`${row.match.item.kind}:${row.match.item.id}`}
                index={i}
                match={row.match}
                selected={i === selected}
                opening={opening === row.match.item.id}
                onHover={setSelected}
                onChoose={() => choose(row)}
              />
            ),
          )}
          {!loading && rows.length === 0 && <div className="switcher-empty">No conversations yet. Type a name to find someone.</div>}
        </div>

        <footer className="switcher-footer">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> to navigate
          </span>
          <span>
            <kbd>↵</kbd> to open
          </span>
          <span>
            <kbd>#</kbd> channels <kbd>@</kbd> people
          </span>
        </footer>
      </div>
    </div>,
    document.body,
  )
}

function rowProps(index: number, selected: boolean, onHover: (i: number) => void, onChoose: () => void) {
  return {
    id: `switcher-row-${index}`,
    'data-index': index,
    role: 'option',
    'aria-selected': selected,
    className: `switcher-row ${selected ? 'selected' : ''}`,
    // mousemove (not mouseenter) so scrolling the list under a still cursor doesn't steal the selection.
    onMouseMove: () => !selected && onHover(index),
    onClick: onChoose,
  } as const
}

function MatchRow({
  index,
  match,
  selected,
  opening,
  onHover,
  onChoose,
}: {
  index: number
  match: SwitcherMatch<Candidate>
  selected: boolean
  opening: boolean
  onHover: (i: number) => void
  onChoose: () => void
}) {
  const { item, highlight } = match
  const u = item.user
  const org = useExternalOrg(u)
  const real = u ? fullName(u) : ''
  const sub =
    item.kind === 'dm' || item.kind === 'person'
      ? [real && real !== displayName(u) ? real : '', org?.name, u?.profile?.title, item.inactive ? 'Deactivated' : ''].filter(Boolean).join(' · ')
      : item.kind === 'group'
        ? 'Group message'
        : item.kind === 'browse'
          ? [item.conv?.num_members ? `${item.conv.num_members} members` : '', item.detail].filter(Boolean).join(' · ')
          : [item.inactive ? 'Archived' : '', item.detail].filter(Boolean).join(' · ')
  const tag = item.kind === 'browse' ? 'Join' : item.kind === 'person' ? 'Message' : null
  const status = u?.profile?.status_emoji?.replace(/:/g, '')

  return (
    <div {...rowProps(index, selected, onHover, onChoose)} data-kind={item.kind}>
      <span className="switcher-icon">
        {item.kind === 'person' ? <Avatar user={u} size={20} /> : item.conv ? <ConversationIcon conv={item.conv} size={16} /> : <Icon name="hash" size={16} />}
      </span>
      <span className="switcher-text">
        <span className={`switcher-label ${item.unread ? 'unread' : ''}`}>
          <span className="switcher-name">{highlightRuns(item.label, highlight).map((r, i) => (r.hit ? <mark key={i}>{r.text}</mark> : r.text))}</span>
          {status && <Emoji name={status} size={14} />}
        </span>
        {sub && <span className="switcher-sub">{sub}</span>}
      </span>
      {opening ? (
        <Spinner size={14} />
      ) : item.mentions ? (
        <span className="switcher-badge">{item.mentions}</span>
      ) : item.unread ? (
        <span className="switcher-dot" aria-label="Unread" />
      ) : null}
      {tag && <span className="switcher-tag">{tag}</span>}
      {selected && <kbd className="switcher-enter">↵</kbd>}
    </div>
  )
}

function SearchRow({ index, query, selected, onHover, onChoose }: { index: number; query: string; selected: boolean; onHover: (i: number) => void; onChoose: () => void }) {
  return (
    <div {...rowProps(index, selected, onHover, onChoose)} data-kind="search">
      <span className="switcher-icon">
        <Icon name="search" size={16} />
      </span>
      <span className="switcher-text">
        <span className="switcher-label">
          Search messages for <strong>“{query}”</strong>
        </span>
      </span>
      {selected && <kbd className="switcher-enter">↵</kbd>}
    </div>
  )
}
